import * as React from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { cn } from "../../utils/cn";
import { Avatar } from "./avatar";
import { Button } from "./button";

export interface SidebarItemConfig {
  id: string;
  label: string;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
}

export interface SidebarSectionConfig {
  title?: React.ReactNode;
  items: SidebarItemConfig[];
}

export interface SidebarUser {
  name: string;
  role?: string;
  image?: string | null;
}

export interface SidebarProps extends React.HTMLAttributes<HTMLElement> {
  sections?: SidebarSectionConfig[];
  defaultCollapsed?: boolean;
  brand?: React.ReactNode;
  user?: SidebarUser;
  footer?: React.ReactNode;
  children?: React.ReactNode;
}

function readCollapsed(defaultCollapsed: boolean) {
  if (typeof window === "undefined") {
    return defaultCollapsed;
  }
  return window.localStorage.getItem("sidebar-collapsed") === "true" || defaultCollapsed;
}

function Sidebar({ sections, defaultCollapsed = false, brand, user, footer, children, className, ...props }: SidebarProps) {
  const [collapsed, setCollapsed] = React.useState(() => readCollapsed(defaultCollapsed));
  const itemRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

  React.useEffect(() => {
    window.localStorage.setItem("sidebar-collapsed", String(collapsed));
  }, [collapsed]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    const focusable = itemRefs.current.filter(Boolean) as HTMLButtonElement[];
    const currentIndex = focusable.findIndex((item) => item === document.activeElement);
    if ((event.key === "j" || event.key === "ArrowDown") && focusable.length) {
      event.preventDefault();
      focusable[(currentIndex + 1 + focusable.length) % focusable.length]?.focus();
    }
    if ((event.key === "k" || event.key === "ArrowUp") && focusable.length) {
      event.preventDefault();
      focusable[(currentIndex - 1 + focusable.length) % focusable.length]?.focus();
    }
    if (event.key === "Enter" && document.activeElement instanceof HTMLButtonElement) {
      document.activeElement.click();
    }
  };

  let itemIndex = 0;

  return (
    <aside
      className={cn(
        "flex h-full flex-col border-r bg-sidebar text-sidebar-foreground transition-[width] duration-normal ease-spring",
        collapsed ? "w-16" : "w-64",
        className,
      )}
      data-collapsed={collapsed}
      onKeyDown={handleKeyDown}
      {...props}
    >
      <div className="mb-2 flex min-h-14 items-center justify-between gap-2 border-b border-border/50 px-3 pb-4 pt-4">
        <div className={cn("min-w-0", collapsed && "sr-only")}>{brand}</div>
        <Button
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="touch-target shrink-0 transition-transform duration-fast ease-out hover:scale-105 active:scale-95 lg:size-9"
          size="icon"
          type="button"
          variant="ghost"
          onClick={() => setCollapsed((current) => !current)}
        >
          {collapsed ? (
            <ChevronRightIcon className="size-4 transition-transform duration-fast ease-out" />
          ) : (
            <ChevronLeftIcon className="size-4 transition-transform duration-fast ease-out" />
          )}
        </Button>
      </div>
      <nav className="flex flex-1 flex-col gap-6 overflow-y-auto px-3 py-4">
        {children ??
          sections?.map((section, sectionIndex) => (
            <SidebarSection key={sectionIndex} title={section.title} collapsed={collapsed}>
              {section.items.map((item) => {
                const refIndex = itemIndex;
                itemIndex += 1;
                return (
                  <SidebarItem
                    key={item.id}
                    ref={(node) => {
                      itemRefs.current[refIndex] = node;
                    }}
                    collapsed={collapsed}
                    {...item}
                  />
                );
              })}
            </SidebarSection>
          ))}
      </nav>
      {(user || footer) ? (
        <div className={cn("mt-2 grid gap-3 border-t border-border/50 px-3 pb-[calc(var(--safe-bottom)+0.75rem)] pt-4 lg:pb-3", collapsed && "px-2")}>
          {user ? (
            <div className={cn("flex items-center gap-2", collapsed && "justify-center")}>
              <Avatar name={user.name} image={user.image} size="sm" />
              <div className={cn("min-w-0", collapsed && "sr-only")}>
                <p className="truncate text-sm font-medium">{user.name}</p>
                {user.role ? <p className="truncate text-xs text-muted-foreground">{user.role}</p> : null}
              </div>
            </div>
          ) : null}
          {footer}
        </div>
      ) : null}
    </aside>
  );
}

function SidebarSection({
  title,
  collapsed,
  className,
  ...props
}: Omit<React.HTMLAttributes<HTMLDivElement>, "title"> & { title?: React.ReactNode; collapsed?: boolean }) {
  return (
    <section className={cn("grid gap-1", className)} {...props}>
      {title ? (
        <h2
          className={cn(
            "mb-1 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70",
            collapsed && "sr-only",
          )}
        >
          {title}
        </h2>
      ) : null}
      <div className="grid gap-0.5">{props.children}</div>
    </section>
  );
}

const SidebarItem = React.forwardRef<
  HTMLButtonElement,
  SidebarItemConfig & React.ButtonHTMLAttributes<HTMLButtonElement> & { collapsed?: boolean }
>(({ label, icon, badge, active, disabled, onSelect, collapsed, className, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      "group flex min-h-11 w-full items-center gap-2.5 rounded-md px-3 text-left text-sm font-medium text-sidebar-foreground transition-colors duration-fast ease-out hover:bg-muted/60 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 lg:h-9 lg:min-h-9",
      active &&
        "bg-[color-mix(in_oklch,oklch(var(--module-accent))_12%,transparent)] text-[oklch(var(--module-accent))]",
      collapsed && "size-11 justify-center px-0 lg:size-9",
      className,
    )}
    data-active={active ? "true" : undefined}
    disabled={disabled}
    title={collapsed ? label : undefined}
    type="button"
    onClick={onSelect}
    {...props}
  >
    {icon ? <span className="grid size-5 shrink-0 place-items-center text-current">{icon}</span> : null}
    <span className={cn("min-w-0 flex-1 truncate", collapsed && "sr-only")}>{label}</span>
    {badge ? (
      <span
        className={cn(
          "ml-auto min-w-5 rounded-full bg-muted px-1.5 text-center text-xs font-medium leading-5 text-muted-foreground",
          active && "bg-[color-mix(in_oklch,oklch(var(--module-accent))_18%,transparent)] text-[oklch(var(--module-accent))]",
          collapsed && "sr-only",
        )}
      >
        {badge}
      </span>
    ) : null}
  </button>
));
SidebarItem.displayName = "SidebarItem";

function SidebarFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("grid gap-2", className)} {...props} />;
}

export { Sidebar, SidebarFooter, SidebarItem, SidebarSection };
